/**
 * @fileoverview Root layout component providing navigation, authentication,
 * and app shell structure. Composes AppLayout with header slots.
 */

import * as React from 'react';
import { Suspense } from 'react';
import { LinearProgress } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuthSession } from '../../features/auth';
import { AppSpeedDial } from '../../features/speed-dial';
import { AppLayout } from '../layouts/app-layout/AppLayout';
import { AppBarTitle } from '../layouts/app-layout/AppBarTitle';
import { AppBarActions } from '../layouts/app-layout/AppBarActions';
import { SidebarFooter } from '../layouts/app-layout/SidebarFooter';
import { ErrorBoundary } from './ErrorBoundary';

export default function RootLayout(): React.JSX.Element {
  const { session, loading } = useAuthSession();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ width: '100%' }}>
        <LinearProgress />
      </div>
    );
  }

  if (!session) {
    // Come back to this exact page (including its query string) after sign-in.
    const callbackUrl = `${location.pathname}${location.search}${location.hash}`;
    const redirectTo = `/sign-in?callbackUrl=${encodeURIComponent(callbackUrl)}`;

    return <Navigate to={redirectTo} replace />;
  }

  return (
    <ErrorBoundary>
      <AppLayout
        slots={{
          sidebarFooter: SidebarFooter,
        }}
        slotProps={{
          header: {
            slots: {
              appTitle: AppBarTitle,
              toolbarActions: AppBarActions,
            },
          },
        }}
      >
        <Suspense fallback={<LinearProgress />}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </Suspense>
        <AppSpeedDial />
      </AppLayout>
    </ErrorBoundary>
  );
}
